import React from 'react';
import { useMutation } from 'react-query';
import {
  Modal,
  ModalContent,
  ModalCloseButton,
  ModalTitle,
} from '@raptormaps/modal';
import { Text } from '@raptormaps/text';
import { TextInput } from '@raptormaps/text-input';
import { Button } from '@raptormaps/button';
import { useToast } from '@raptormaps/toast';
import { getUpdateOrgMutation } from '../../api/services/orgs';
import styled from 'styled-components';
import theme from '@raptormaps/theme';

const ModalBody = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  min-width: 420px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${theme.colors.slate_200};
`;

interface EditOrgModalProps {
  open: boolean;
  onClose: () => void;
  orgId: number;
  currentName: string;
}

export function EditOrgModal({
  open,
  onClose,
  orgId,
  currentName,
}: EditOrgModalProps) {
  const toast = useToast();
  const { mutate, isLoading } = useMutation(getUpdateOrgMutation(orgId));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed =
      new FormData(e.currentTarget).get('org-name')?.toString().trim() ?? '';
    if (!trimmed || trimmed === currentName) return;
    mutate(
      { name: trimmed },
      {
        onSuccess: () => {
          toast.success('Organization name updated successfully.');
          onClose();
        },
        onError: () => {
          toast.error('Failed to update organization name. Please try again.');
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent>
        <ModalBody key={`${open}-${currentName}`} onSubmit={handleSubmit}>
          <ModalHeader>
            <ModalTitle>
              <Text variant="h4">Edit Organization</Text>
            </ModalTitle>
            <ModalCloseButton />
          </ModalHeader>

          <FieldGroup>
            <Text
              as="label"
              htmlFor="org-name"
              variant="paragraph_small_bold"
              color={theme.colors.neutral_700}
            >
              Organization Name
            </Text>
            <TextInput
              id="org-name"
              name="org-name"
              defaultValue={currentName}
              placeholder="Enter organization name"
              disabled={isLoading}
            />
          </FieldGroup>

          <ModalFooter>
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </ModalFooter>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
